import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { LOGIN_URL } from '../lib/auth-client.js';
import { PasswordField, Note, Problem, button } from './form.js';
import { verifyPassword } from '../lib/email-change.js';

/**
 * Prove it is still you, before the Security page shows you the thing you came
 * to change.
 *
 * ── WHY IT IS A STEP AND NOT A FIELD BESIDE THE FORM ─────────────────
 *
 * Jon, 2026-09-01. Both sections that needed a credential asked for it inline,
 * next to the thing being changed — so the form was fully visible and fully
 * fillable to somebody who had walked up to an unlocked laptop, and the refusal
 * arrived only after they had typed everything. Asking first, and revealing the
 * field afterwards, means a borrowed session gets nowhere and a legitimate
 * person finds out in one keystroke rather than at the end of a form.
 *
 * ── WHAT IT ACTUALLY GUARANTEES, WHICH IS LESS THAN IT LOOKS ─────────
 *
 * READ THIS BEFORE REMOVING A PASSWORD FROM ANY CALL SITE.
 *
 * This is a UX gate, NOT the security boundary. `/reauthenticate` changes
 * nothing and grants nothing; the operation the caller goes on to perform still
 * sends the credential and is still checked server-side on its own request.
 * That is why `onVerified` hands the password BACK rather than swallowing it —
 * `requestChange` needs it, and `twoFactor.enable` needs it.
 *
 * The plausible-sounding next step is "the gate already checked it, so stop
 * sending it". That would move the real check onto a boolean held in a browser,
 * which is exactly the arrangement this project keeps writing comments about.
 *
 * ── THE THREE SHAPES OF ACCOUNT ──────────────────────────────────────
 *
 * A PASSWORD, which is 37 of the 38 real accounts today: ask for it.
 *
 * TWO-FACTOR as well: still ask for the password. The gate deliberately does
 * NOT ask for a TOTP code even though `/two-factor/verify-totp` would accept
 * one from a signed-in caller, because the operations behind this gate all take
 * a password and none of them takes a code — asking for the code would prove
 * something and then still need the password, which is two prompts for one
 * step. Worth revisiting if an operation appears that a code alone can
 * authorise.
 *
 * NEITHER — no password at all. There is nothing to ask for, and the server
 * agrees: `refuseWithoutCurrentPassword` exempts an account with no credential
 * row, because demanding one would make the address permanently unchangeable
 * for exactly the people RidgeWill/auth#324 exists for. So the gate does not
 * invent a challenge it cannot check. It says so, points at setting a password,
 * and lets the operation proceed under the server's own exemption.
 */
export type Credential = { password?: string };

export function SecurityGate({
  action,
  hasPassword,
  onVerified,
  onCancel,
}: {
  /** Completes "to <action>", lower case: "change your email address". */
  action: string;
  /** From the sign-in methods lookup the Security screen already does. */
  hasPassword: boolean;
  /** Called with the credential the OPERATION still has to send. */
  onVerified: (credential: Credential) => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * NO PASSWORD, SO NO CHALLENGE.
   *
   * Not a silent pass-through: somebody who opened this expecting to be asked
   * should be told why they were not, or the missing step reads as a bug in
   * the page. It also names the fix, because an account in this state has one
   * way in and would benefit from a second.
   */
  if (!hasPassword) {
    return (
      <>
        <Note>
          This account does not have a password, so there is nothing to confirm before you{' '}
          {action}.
        </Note>
        <p style={{ marginTop: '1rem', lineHeight: 1.5, color: C.textMid }}>
          Setting one adds a way to sign in and a step in front of changes like this.
        </p>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <button type="button" style={button} onClick={() => onVerified({})}>
            Continue
          </button>
          <a
            href={`${LOGIN_URL}/change-password?returnTo=account`}
            style={{ ...button, display: 'inline-block', textDecoration: 'none' }}
          >
            Set a password
          </a>
        </div>
      </>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const message = await verifyPassword(password);
    setBusy(false);
    if (message) {
      setError(message);
      return;
    }
    /**
     * The password goes ON, deliberately. See the header: the operation checks
     * it again server-side, and this gate is the order of a form rather than a
     * permission.
     *
     * Not cleared here either, because the caller still needs it. Whoever
     * closes the modal is responsible for dropping it, which is why every
     * caller keeps it in state that dies with the dialog.
     */
    onVerified({ password });
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <Note>Confirm your password to {action}.</Note>

      <div style={{ marginTop: '1rem' }}>
        <PasswordField
          id="security-gate-password"
          label="Your current password"
          value={password}
          autoComplete="current-password"
          onChange={setPassword}
        />
      </div>

      {error ? <Problem>{error}</Problem> : null}

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
        <button type="submit" disabled={busy || password.length === 0} style={button}>
          {busy ? 'Checking…' : 'Continue'}
        </button>
        <button type="button" onClick={onCancel} style={button}>
          Cancel
        </button>
      </div>
    </form>
  );
}
